import { GRID_ROOT_GROUP_ID, type GridGroupNode, type GridRowId } from '@mui/x-data-grid-premium';
import { RiArrowDownSLine } from '@remixicon/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { AgButton } from 'src/components/ui';
import { useDebounce } from 'src/hooks/useDebounce';
import type { GridApiRef } from 'src/modules/grids';
import { useGridCore } from 'src/providers/GridCoreProvider';
import { useDebounce as useDebouncedValue } from 'usehooks-ts';

type ExpandMode = 'group' | 'detailPanel';

// MUI X 8+ `filteredRowsLookup` only lists filtered-OUT rows, so groups are read from the row tree.
const getGroupNodes = (api: NonNullable<GridApiRef['current']>): GridGroupNode[] =>
  Object.values(api.state.rows.tree).filter(
    (node): node is GridGroupNode => node?.type === 'group' && node.id !== GRID_ROOT_GROUP_ID
  );

export default function ExpandAllButton({
  apiRef,
  mode = 'group',
  'data-testid': dataTestId,
}: {
  apiRef: GridApiRef;
  mode?: ExpandMode;
  'data-testid'?: string;
}) {
  const setExpandedGroups = useGridCore((store) => store.setExpandedGroups);
  const [allExpanded, setAllExpanded] = useState(false);

  const isAllGroupsExpanded = useCallback(() => {
    const api = apiRef.current;
    if (!api) return false;

    const groupNodes = getGroupNodes(api);

    if (!groupNodes.length) {
      return false;
    }

    return groupNodes.every((node) => node.childrenExpanded);
  }, [apiRef]);

  const isAllDetailPanelsExpanded = useCallback(() => {
    const api = apiRef.current;
    if (!api) return false;

    const allRowIds = api.getAllRowIds();

    if (!allRowIds?.length) {
      return false;
    }

    const expandedIds = api.getExpandedDetailPanels();

    return expandedIds.size > 0 && expandedIds.size >= allRowIds.length;
  }, [apiRef]);

  const checkExpansion = useCallback(() => {
    return mode === 'detailPanel' ? isAllDetailPanelsExpanded() : isAllGroupsExpanded();
  }, [mode, isAllDetailPanelsExpanded, isAllGroupsExpanded]);

  const prevExpandedRef = useRef<Set<GridRowId>>(new Set());

  const handleRowExpansionChange = useDebounce(() => {
    // Guard against being flushed after the grid unmounts (useDebounce fires
    // pending calls on unmount). Without this, apiRef.current is null and the
    // reads below throw, crashing the whole tab via the error boundary.
    if (!apiRef.current) {
      return;
    }

    if (mode === 'detailPanel') {
      const current = apiRef.current.getExpandedDetailPanels();
      const prev = prevExpandedRef.current;
      if (current.size === prev.size && [...current].every((id) => prev.has(id))) {
        return;
      }
      prevExpandedRef.current = new Set(current);
    }
    setAllExpanded(checkExpansion());
  }, 10);

  useEffect(() => {
    const api = apiRef.current;
    if (!api) return;

    const unsubscribers = [api.subscribeEvent('rowExpansionChange', handleRowExpansionChange)];

    if (mode === 'detailPanel') {
      unsubscribers.push(api.subscribeEvent('detailPanelsExpandedRowIdsChange', handleRowExpansionChange));
    }

    handleRowExpansionChange();

    return () => {
      unsubscribers.forEach((unsub) => unsub());
    };
  }, [apiRef, handleRowExpansionChange, mode]);

  const handleToggleAllDetailPanels = () => {
    const api = apiRef.current;
    if (!api) return;

    const allRowIds = api.getAllRowIds();
    const newState = !allExpanded;

    api.setExpandedDetailPanels(new Set(newState ? allRowIds : []));
    setAllExpanded(newState);
  };

  const handleToggleAllGroups = () => {
    const api = apiRef.current;
    if (!api) return;

    const groupRowIds = getGroupNodes(api);

    const newExpansionState = !allExpanded;

    const allExpandedGroups = groupRowIds.reduce<Record<string, boolean>>((acc, { id, groupingKey }) => {
      if (groupingKey == null || typeof groupingKey === 'boolean') {
        console.warn('Grouping key is missing or a boolean, cannot expand/collapse group');

        return acc;
      }

      api.setRowChildrenExpansion(id, newExpansionState);

      acc[groupingKey] = newExpansionState;

      return acc;
    }, {});

    setExpandedGroups(allExpandedGroups);
  };

  const handleToggleAll = mode === 'detailPanel' ? handleToggleAllDetailPanels : handleToggleAllGroups;

  // debounce the value a little so that if the state is changing a lot, the button doesn't freak out.
  const isAllExpanded = useDebouncedValue(allExpanded, 50);

  return (
    <AgButton variant="white" shape="rounded" onPress={handleToggleAll} data-testid={dataTestId}>
      <RiArrowDownSLine
        size={18}
        className={`transition-transform duration-200 ${isAllExpanded ? '-rotate-90' : ''}`}
      />
      {isAllExpanded ? 'Collapse All' : 'Expand All'}
    </AgButton>
  );
}
